/**
 * Job Application API Service
 * Handles job application submissions to the Talenty backend
 */

import { getCompanyFromUrl } from '../utils/company';

// Job application interfaces
export interface JobApplicationRequest {
  placementId?: string;
  placementSnapshotId?: string;
  publicationOperationId?: string;
  jobId?: string;
  jobSlug?: string;
  jobTitle?: string;
  companySlug?: string;
  applicationMethod: 'form' | 'linkedin';
  firstName?: string;
  lastName?: string;
  email?: string;
  linkedinUrl?: string;
  germanLevel?: string;
  yearsOfExperience?: string;
  workPermit?: string;
  documents?: File[];
}

export interface DocumentUploadResult {
  fileName: string;
  fileSize: number;
  success: boolean;
  storageUrl?: string;
  error?: string;
}

export interface JobApplicationResponse {
  applicationId: string;
  success: boolean;
  message: string;
  jobTitle?: string;
  jobSlug?: string;
  uploadedDocuments: DocumentUploadResult[];
  submittedAt: string;
  nextSteps?: string;
}

class JobApplicationApiService {
  private baseUrl: string;

  constructor() {
    // Configure API base URL via env with safe fallback to production backend
    this.baseUrl = import.meta.env.VITE_API_BASE_URL ||
      `https://ts.talenty.ai`;

  }

  /**
   * Submit a job application with form data and optional file uploads
   */
  async submitApplication(applicationData: JobApplicationRequest): Promise<JobApplicationResponse> {
    const formData = new FormData();

    // Add company slug from URL if not provided
    const companySlug = applicationData.companySlug || getCompanyFromUrl();
    formData.append('companySlug', companySlug);

    // Add job reference data
    if (applicationData.jobId) formData.append('jobId', applicationData.jobId);
    if (applicationData.jobSlug) formData.append('jobSlug', applicationData.jobSlug);
    if (applicationData.jobTitle) formData.append('jobTitle', applicationData.jobTitle);
    if (applicationData.placementId) formData.append('placementId', applicationData.placementId);
    if (applicationData.placementSnapshotId) formData.append('placementSnapshotId', applicationData.placementSnapshotId);
    if (applicationData.publicationOperationId) formData.append('publicationOperationId', applicationData.publicationOperationId);

    // Add application method
    formData.append('applicationMethod', applicationData.applicationMethod);

    // Add email (required for both methods)
    if (applicationData.email) formData.append('email', applicationData.email);

    // Add form data based on application method
    if (applicationData.applicationMethod === 'form') {
      if (applicationData.firstName) formData.append('firstName', applicationData.firstName);
      if (applicationData.lastName) formData.append('lastName', applicationData.lastName);
    }

    // Add LinkedIn URL (for both methods)
    if (applicationData.linkedinUrl) formData.append('linkedinUrl', applicationData.linkedinUrl);
    
    // Add job-specific questions
    if (applicationData.germanLevel) formData.append('germanLevel', applicationData.germanLevel);
    if (applicationData.yearsOfExperience) formData.append('yearsOfExperience', applicationData.yearsOfExperience);
    if (applicationData.workPermit) formData.append('workPermit', applicationData.workPermit);
    
    // Add documents
    if (applicationData.documents && applicationData.documents.length > 0) {
      applicationData.documents.forEach((file) => {
        formData.append('documents', file);
      });
    }
    
    const response = await fetch(`${this.baseUrl}/api/jobs/applications/submit`, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
    });
    
    if (!response.ok) {
      throw new Error(`Die Bewerbung konnte nicht gesendet werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get application details by ID
   */
  async getApplication(applicationId: string): Promise<JobApplicationResponse> {
    const response = await fetch(`${this.baseUrl}/api/jobs/applications/${applicationId}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Die Bewerbung wurde nicht gefunden.');
      }
      throw new Error(`Die Bewerbung konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Test API connectivity
   */
  async testConnection(): Promise<{ message: string; timestamp: string }> {
    const response = await fetch(`${this.baseUrl}/api/jobs/applications/test`);
    
    if (!response.ok) {
      throw new Error(`Der Bewerbungsservice ist nicht erreichbar (Status ${response.status}).`);
    }
    
    return response.json();
  }
}

export const jobApplicationApi = new JobApplicationApiService();
